'use client';
import { useEffect, useRef } from 'react';
import { useAccount, useDisconnect } from 'wagmi';
import { SovButton } from '../ui/sov-button';
import { truncateAddress } from '@/lib/sovereign';
import { WalletConnectModal, useWalletModal } from './wallet-connect-modal';
import { useAuth } from '@/hooks/use-auth';

interface WalletButtonProps {
  variant?: 'primary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

export function WalletButton({ variant = 'outline', size = 'sm', fullWidth = false }: WalletButtonProps) {
  const { address, isConnected, chainId } = useAccount();
  const { disconnect } = useDisconnect();
  const { isOpen, openModal, closeModal } = useWalletModal();
  const { isAuthenticated, isSigningIn, signIn, signOut, error } = useAuth();
  const prevConnected = useRef(false);

  useEffect(() => {
    if (isConnected && address && !isAuthenticated && !isSigningIn && !prevConnected.current) {
      prevConnected.current = true;
      signIn();
    }
    if (!isConnected) {
      prevConnected.current = false;
    }
  }, [isConnected, address, chainId, isAuthenticated, isSigningIn, signIn]);

  if (isConnected && address) {
    const statusColor = isAuthenticated ? 'bg-brand' : (isSigningIn ? 'bg-amber-400' : 'bg-orange-400');
    const shouldPulse = !isAuthenticated;

    const handleClick = () => {
      if (!isAuthenticated && !isSigningIn) {
        signIn();
      } else {
        signOut();
        disconnect();
      }
    };

    return (
      <div className="flex flex-col items-start gap-1">
        <SovButton variant={variant} size={size} fullWidth={fullWidth} onClick={handleClick}>
          <span className={`h-2 w-2 rounded-full ${statusColor} ${shouldPulse ? 'animate-pulse' : ''}`} />
          {isSigningIn ? 'Signing...' : !isAuthenticated ? 'Sign In' : truncateAddress(address)}
        </SovButton>
        {error && !isSigningIn && (
          <p className="text-xs text-red-400 max-w-[160px] truncate" title={error}>{error}</p>
        )}
      </div>
    );
  }

  return (
    <>
      <SovButton
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        onClick={openModal}
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.293 9.293L14.707 3.707C14.52 3.52 14.265 3.414 14 3.414S13.48 3.52 13.293 3.707L11.586 5.414L16.586 10.414L18.293 8.707A1 1 0 0120.293 9.293zM10.879 6.121L3.707 13.293A1 1 0 003.414 14c0 .265.106.52.293.707L9.293 20.293A1 1 0 0010 20.586c.265 0 .52-.106.707-.293L17.879 13.121 12.879 8.121l-2 2-2-2zM11 15h2v2h-2v-2zm2-4h2v2h-2v-2z"/>
        </svg>
        Connect Wallet
      </SovButton>
      <WalletConnectModal open={isOpen} onClose={closeModal} />
    </>
  );
}
